import type { Activity } from "./activity";
import type { GenericReview } from "./config/types";
import type { OrderItemDetails } from "./review";

export interface ProgressBarSegment {
  label: string;
}

export type ProductLabel = {
  name: string | null;
  productGroupName: string | null;
};

const formatProductLabel = (
  name: string,
  productGroupName: string | null | undefined,
): string =>
  productGroupName && productGroupName !== name
    ? `${productGroupName} \u2013 ${name}`
    : name;

function getProductNames({
  activity,
  orderItemDetails,
  orderItemLabels,
}: {
  activity: Activity;
  orderItemDetails?: OrderItemDetails;
  orderItemLabels?: ProductLabel[];
}): string[] {
  // Production review: the server has resolved the actual order items the
  // user is reviewing (one for `single`/`multi`/`swatchSelector`, N for
  // bundles). Use that as the source of truth so the segment count and
  // labels reflect what the user will actually go through.
  if (orderItemLabels) {
    return orderItemLabels.map((item) =>
      formatProductLabel(item.name ?? "", item.productGroupName),
    );
  }

  const products = activity.products;
  if (products.type === "single") {
    // Single-product preview falls through to here when no order context
    // is available; prefer order item details if present.
    if (orderItemDetails) {
      return [
        formatProductLabel(
          orderItemDetails.name ?? "",
          orderItemDetails.productGroupName,
        ),
      ];
    }
    return [
      formatProductLabel(
        products.product.name,
        products.product.productGroupName,
      ),
    ];
  }
  // Preview only: no ticket/order, so fall back to the activity catalogue
  // to give the config UI something to render. Production review must
  // pass `orderItemLabels` to avoid this branch \u2014 for `multi`/`swatchSelector`
  // the catalogue is the *full* set of selectable variants, not the one
  // the user actually got.
  return products.product.products.map((p) => {
    const item = p as { name?: string; productGroupName?: string | null };
    return formatProductLabel(item.name ?? "", item.productGroupName);
  });
}

const getPageType = (page: GenericReview["pages"][number]): string =>
  Object.keys(page)[0] ?? "";

/**
 * Build the ordered list of progress-bar segments for a review flow.
 *
 * `per_product_page` entries expand to one segment per product (one for a
 * single-product activity, N for a bundle). `standard_page` entries become a
 * single unlabelled segment. `thank_you` (and any unknown page types) are
 * intentionally omitted because the bar represents the user's journey *to*
 * completion, not the post-completion state.
 */
export function buildReviewProgressSegments({
  config,
  activity,
  orderItemDetails,
  orderItemLabels,
}: {
  config: GenericReview;
  activity: Activity;
  orderItemDetails?: OrderItemDetails;
  orderItemLabels?: ProductLabel[];
}): ProgressBarSegment[] {
  const productNames = getProductNames({
    activity,
    orderItemDetails,
    orderItemLabels,
  });
  const segments: ProgressBarSegment[] = [];
  for (const page of config.pages) {
    const type = getPageType(page);
    if (type === "per_product_page") {
      for (const name of productNames) {
        segments.push({ label: name });
      }
    } else if (type === "standard_page") {
      segments.push({ label: "" });
    }
  }
  return segments;
}

/**
 * Compute the 1-based current segment for the given page index. Reaching the
 * `thank_you` page surfaces a synthetic "all done" state by pointing one
 * past the last segment, which the `<ProgressBar/>` component renders as
 * fully complete.
 */
export function getReviewCurrentPage({
  config,
  activity,
  orderItemDetails,
  orderItemLabels,
  pageIndex,
}: {
  config: GenericReview;
  activity: Activity;
  orderItemDetails?: OrderItemDetails;
  orderItemLabels?: ProductLabel[];
  pageIndex: number;
}): number {
  const productNames = getProductNames({
    activity,
    orderItemDetails,
    orderItemLabels,
  });
  const currentPage = config.pages[pageIndex];
  if (currentPage && getPageType(currentPage) === "thank_you") {
    return (
      buildReviewProgressSegments({
        config,
        activity,
        orderItemDetails,
        orderItemLabels,
      }).length + 1
    );
  }
  let priorSegments = 0;
  for (let i = 0; i < pageIndex; i++) {
    const type = getPageType(config.pages[i]);
    if (type === "per_product_page") {
      priorSegments += productNames.length;
    } else if (type === "standard_page") {
      priorSegments += 1;
    }
  }
  return priorSegments + 1;
}
